import type {
  MetaArtifact,
  PointAvailability,
  PricesArtifact,
  SiteDetail,
  StationsFeature,
  StatusArtifact,
} from '@ev/shared'
import { buildSiteDirectory, type SiteDirectoryEntry } from '../search/directory'
import { buildUntrackedOperators, type UntrackedOperator } from '../search/untracked'
import { artifactClient } from './artifacts'

/**
 * One read per artifact per page load, shared by everything that wants it.
 *
 * The three panels need overlapping slices of the same five files. Left to
 * themselves the map and the search box both pull the national
 * `stations.geojson`, and the availability facet and the detail panel both pull
 * `status.json`, the largest file of the set. Everything goes through here
 * instead, so a second caller gets the in-flight promise rather than a second
 * request.
 *
 * Nothing here is eager: whoever asks first triggers the read. The map asks for
 * stations on mount, the availability facet asks for status the first time it is
 * ticked, and a visitor who never opens a site never pays for `prices.json`.
 *
 * A rejected promise is evicted, so a retry button retries for real instead of
 * replaying the cached failure.
 */

const evictOnFailure = <T>(promise: Promise<T>, evict: () => void): Promise<T> => {
  promise.catch(evict)
  return promise
}

// ---------------------------------------------------------------------------
// stations.geojson, and everything derived from it
// ---------------------------------------------------------------------------

/**
 * The national feature set plus the indexes built from it.
 *
 * Derived once, when the artifact lands: the search haystacks and the id lookup
 * are the same data viewed two other ways, and rebuilding either per keystroke
 * or per filter pass is what makes a 9 000-site map feel slow.
 */
export interface StationIndex {
  readonly features: readonly StationsFeature[]
  readonly directory: readonly SiteDirectoryEntry[]
  /** Site id -> its folded search haystack, for the filter predicate. */
  readonly haystackById: ReadonlyMap<string, string>
  readonly byId: ReadonlyMap<string, StationsFeature>
  /** The networks inside the `other` bucket, biggest first. */
  readonly untracked: readonly UntrackedOperator[]
  /** When the pipeline generated this artifact. */
  readonly generated: string
}

/**
 * Rebuild one feature as a plain object.
 *
 * funtypes' `safeParse` reconstructs what it validates, and the objects it
 * returns have a **null prototype**. MapLibre hands the GeoJSON to its
 * clustering worker through its own serializer, which reads
 * `value.constructor._classRegistryKey` - and on a null-prototype object
 * `constructor` is `undefined`, so the whole payload throws inside the worker
 * and not one dot is ever drawn. The map still reports itself as loaded, which
 * is what makes this worth a comment: it fails as a blank basemap, not as an
 * error.
 *
 * So the validated values are copied back onto `Object.prototype` here, once,
 * on the one path that crosses a `postMessage` boundary. Validation keeps its
 * field-path errors; MapLibre gets objects it can serialize.
 */
const toPlainFeature = (feature: StationsFeature): StationsFeature => ({
  type: 'Feature',
  geometry: {
    type: 'Point',
    coordinates: [feature.geometry.coordinates[0], feature.geometry.coordinates[1]],
  },
  properties: {
    ...feature.properties,
    conn: [...feature.properties.conn],
    v: [...feature.properties.v],
  },
})

export const buildStationIndex = (
  rawFeatures: readonly StationsFeature[],
  generated: string,
): StationIndex => {
  const features = rawFeatures.map(toPlainFeature)
  const directory = buildSiteDirectory(features)
  const haystackById = new Map<string, string>()
  const byId = new Map<string, StationsFeature>()

  directory.forEach((entry) => {
    haystackById.set(entry.id, entry.haystack)
  })
  features.forEach((feature) => {
    byId.set(feature.properties.id, feature)
  })

  return {
    features,
    directory,
    haystackById,
    byId,
    untracked: buildUntrackedOperators(features),
    generated,
  }
}

export const EMPTY_STATION_INDEX: StationIndex = buildStationIndex([], '')

let stations: Promise<StationIndex> | undefined

/**
 * Fetching, validating and indexing the national artifact.
 *
 * Validation is the expensive half - ~190 ms for 9 000 features on a laptop -
 * and it was worth trying to move off the main thread. Measured, a worker did
 * not help: the structured clone of 9 000 features costs the main thread about
 * what the validation did, so the long task moved rather than disappeared, and
 * the run-to-run difference in total blocking time was inside the noise. The
 * simpler code is therefore the one that ships. See the report in
 * `docs/frontend-performance.md`.
 */
export const loadStationIndex = (): Promise<StationIndex> => {
  stations ??= evictOnFailure(
    artifactClient
      .fetchStations()
      .then((collection) => buildStationIndex(collection.features, collection.generated)),
    () => {
      stations = undefined
    },
  )
  return stations
}

// ---------------------------------------------------------------------------
// status.json, and the availability index derived from it
// ---------------------------------------------------------------------------

export type AvailabilityIndex = ReadonlyMap<string, ReadonlySet<PointAvailability>>

/**
 * Site id -> the availability states actually present at that site.
 *
 * A state with a zero count is left out, so filtering for "free" keeps only
 * sites that have at least one free point right now.
 */
export const buildAvailabilityIndex = (status: StatusArtifact): AvailabilityIndex => {
  const index = new Map<string, ReadonlySet<PointAvailability>>()
  Object.entries(status.sites).forEach(([siteId, site]) => {
    if (site === undefined) return
    const states = new Set<PointAvailability>()
    if (site.free > 0) states.add('free')
    if (site.occupied > 0) states.add('occupied')
    if (site.unavailable > 0) states.add('unavailable')
    if (site.unknown > 0) states.add('unknown')
    index.set(siteId, states)
  })
  return index
}

let status: Promise<StatusArtifact> | undefined

export const loadStatus = (): Promise<StatusArtifact> => {
  status ??= evictOnFailure(artifactClient.fetchStatus(), () => {
    status = undefined
  })
  return status
}

export const loadAvailabilityIndex = (): Promise<AvailabilityIndex> =>
  loadStatus().then(buildAvailabilityIndex)

// ---------------------------------------------------------------------------
// The rest
// ---------------------------------------------------------------------------

let prices: Promise<PricesArtifact> | undefined
let meta: Promise<MetaArtifact> | undefined
const sites = new Map<string, Promise<SiteDetail>>()

export const loadPrices = (): Promise<PricesArtifact> => {
  prices ??= evictOnFailure(artifactClient.fetchPrices(), () => {
    prices = undefined
  })
  return prices
}

/**
 * `meta.json`, fetched once per page load.
 *
 * The footer's freshness line, the stale-data notice and the about page's
 * data-quality tables read the same promise, so they can never quote two
 * different publishes.
 */
export const loadMeta = (): Promise<MetaArtifact> => {
  meta ??= evictOnFailure(artifactClient.fetchMeta(), () => {
    meta = undefined
  })
  return meta
}

export const loadSite = (siteId: string): Promise<SiteDetail> => {
  const cached = sites.get(siteId)
  if (cached !== undefined) return cached

  const pending = evictOnFailure(artifactClient.fetchSite(siteId), () => sites.delete(siteId))
  sites.set(siteId, pending)
  return pending
}

/** Tests reach for this; the app never does. */
export const resetArtifactCache = (): void => {
  stations = undefined
  status = undefined
  prices = undefined
  meta = undefined
  sites.clear()
}
